import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Search, MapPin, Loader2, X } from 'lucide-react';
import { GEOAPIFY_API_KEY } from '../config/api';

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.inputBorder || '#e2e8f0'};
  font-size: 13px;
  background: ${p => p.theme.inputBg || '#f8fafc'};
  color: ${p => p.theme.textPrimary || '#0f172a'};
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5D722C;
    background: ${p => p.theme.cardBg || '#ffffff'};
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }

  &:read-only, &:disabled {
    cursor: default;
    background: ${p => p.theme.tableHeaderBg || '#f8fafc'};
    border-color: ${p => p.theme.sidebarBorder || '#f1f5f9'};
    color: ${p => p.theme.textSecondary || '#64748b'};
    &:focus {
      border-color: ${p => p.theme.sidebarBorder || '#f1f5f9'};
      box-shadow: none;
    }
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 10px;
  color: #94a3b8;
  pointer-events: none;
`;

const ActionIcon = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
  
  &:hover {
    color: #ef4444;
  }
`;

const LoadingIcon = styled(Loader2)`
  position: absolute;
  right: 10px;
  color: #94a3b8;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 300px;
  overflow-y: auto;
`;

const SuggestionItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
  }
`;

const SuggestionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MainText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const SubText = styled.span`
  font-size: 11px;
  color: #64748b;
`;

interface AddressDetails {
  address_line1: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  formatted: string;
}

interface AddressAutocompleteProps {
  placeholder?: string;
  onAddressSelect: (details: AddressDetails) => void;
  onInputChange?: (value: string) => void;
  value?: string;
  name?: string;
  id?: string;
  className?: string; // For styled-component overrides
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  placeholder = "Search for an address...",
  onAddressSelect,
  onInputChange,
  value = "",
  name,
  id,
  className,
  disabled,
  readOnly,
  required
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(searchText)}&filter=countrycode:us&apiKey=${GEOAPIFY_API_KEY}`
      );
      const data = await response.json();
      if (data.features) {
        // ULTRA-STRICT filter: only allow results that are actual address points
        const filtered = data.features.filter((f: any) => {
           const p = f.properties;
           
           // 1. NEVER show broad area types (suburbs, districts, etc.)
           const broadTypes = ['postcode', 'city', 'state', 'country', 'county', 'suburb', 'district', 'village'];
           if (broadTypes.includes(p.result_type)) {
             return false;
           }
           
           // 2. If the user typed a 5-digit number (likely a zipcode), 
           // we ONLY show a result if that number is actually the HOUSE NUMBER or in the NAME.
           // This stops "30312" from showing everything in Atlanta 30312.
           if (/^\d{5}$/.test(searchText.trim())) {
             const isHouseMatch = p.housenumber === searchText.trim();
             const isNameMatch = p.name && p.name.includes(searchText.trim());
             return isHouseMatch || isNameMatch;
           }
           
           // 3. General requirement: must have a street or specific name to be considered an address
           return p.street || p.housenumber || p.name;
        });
        setSuggestions(filtered);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || disabled) return;
    const newVal = e.target.value;
    setInputValue(newVal);
    if (onInputChange) onInputChange(newVal);
    setShowSuggestions(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(newVal);
    }, 300);
  };

  const handleSelectSuggestion = (feature: any) => {
    const props = feature.properties;
    const details: AddressDetails = {
      address_line1: props.street ? `${props.housenumber || ''} ${props.street}`.trim() : (props.address_line1 || props.name || ''),
      city: props.city || '',
      state: props.state_code || props.state || '',
      zip_code: props.postcode || '',
      country: props.country || '',
      formatted: props.formatted || ''
    };

    setInputValue(details.address_line1);
    if (onInputChange) onInputChange(details.address_line1);
    setShowSuggestions(false);
    onAddressSelect(details);
  };

  const handleClear = () => {
    setInputValue('');
    if (onInputChange) onInputChange('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <AutocompleteContainer ref={containerRef} className={className}>
      <InputWrapper>
        <SearchIcon size={16} />
        <StyledInput
          type="text"
          name={name}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => !readOnly && !disabled && inputValue.length >= 3 && setShowSuggestions(true)}
          placeholder={placeholder}
          autoComplete="nope"
          disabled={disabled}
          readOnly={readOnly}
          required={required}
        />
        {loading ? (
          <LoadingIcon size={16} />
        ) : (
          (inputValue && !readOnly && !disabled) && (
            <ActionIcon onClick={handleClear}>
              <X size={16} />
            </ActionIcon>
          )
        )}
      </InputWrapper>

      {showSuggestions && inputValue.length >= 3 && (
        <SuggestionsList>
          {suggestions.length > 0 ? (
            suggestions.map((feature, index) => (
              <SuggestionItem key={index} onClick={() => handleSelectSuggestion(feature)}>
                <MapPin size={16} color="#5D722C" style={{ marginTop: 2, flexShrink: 0 }} />
                <SuggestionText>
                  <MainText>{feature.properties.address_line1 || feature.properties.name}</MainText>
                  <SubText>{feature.properties.address_line2 || feature.properties.formatted}</SubText>
                </SuggestionText>
              </SuggestionItem>
            ))
          ) : !loading ? (
            <SuggestionItem style={{ cursor: 'default' }}>
              <SubText>No matching addresses found. Keep typing...</SubText>
            </SuggestionItem>
          ) : null}
        </SuggestionsList>
      )}
    </AutocompleteContainer>
  );
};

export default AddressAutocomplete;
